/**
 * Geolocation command actions: dispatched by components, handled by middleware
 * to drive `navigator.geolocation` side effects.
 *
 * App-startup init (storage restore + permission query) is a thunk in
 * `geolocation-thunks.ts` so callers can `await` it.
 */

import type { WatchPolicy } from "@/types/geolocation"
import { createAction } from "@reduxjs/toolkit"

/**
 * Request to start watching location.
 * - reason: "user" = explicit user action (may prompt for permission)
 * - reason: "resume" = resuming after visibility change (no prompting, only if granted)
 * - policy: Optional watch policy (default: { pauseOnHidden: true })
 */
export const geoStartRequested = createAction<{
  reason: "user" | "resume"
  policy?: Partial<WatchPolicy>
}>("geo/startRequested")

/**
 * Request to stop watching location.
 * - "user" = explicit user action (clears stored position)
 * - "logout" = user logged out (full reset)
 * - "hidden" = tab became hidden (pauses, keeps storage for resume)
 */
export const geoStopRequested = createAction<"user" | "logout" | "hidden">(
  "geo/stopRequested"
)

/**
 * Tab visibility changed.
 * - "hidden" = tab became hidden (may pause watching)
 * - "visible" = tab became visible (may resume watching)
 */
export const geoVisibilityChanged = createAction<"hidden" | "visible">(
  "geo/visibilityChanged"
)
